import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { COLORS } from "../theme/tokens";

/**
 * WindroosSpinner — herbruikbare laadspinner met Windroos-identiteit.
 *
 * Props:
 *   label  {string|null}  Zichtbaar label onder de spinner. Standaard "Laden" (visueel verborgen).
 *   size   {number}       Diameter in px. Standaard 48.
 *   subtle {boolean}      Kleine, minimalistische variant (halve grootte, alleen ring).
 */
export default function WindroosSpinner({ label, size = 48, subtle = false }) {
  const diameter = subtle ? Math.max(size * 0.5, 20) : size;
  const strokeWidth = subtle ? 2 : Math.max(3, diameter / 14);

  // Gemeenschappelijke keyframes via een <style>-element dat we inline injecteren.
  // We gebruiken een unieke class-naam zodat meerdere instanties naast elkaar werken.
  const spinClass = subtle ? "wr-spin-subtle" : "wr-spin";

  return (
    <>
      <style>{`
        @keyframes wr-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes wr-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.6; }
        }
        @media (prefers-reduced-motion: no-preference) {
          .wr-spin        { animation: wr-rotate 1.4s linear infinite; }
          .wr-spin-subtle { animation: wr-rotate 1.4s linear infinite; }
        }
        @media (prefers-reduced-motion: reduce) {
          .wr-spin        { animation: wr-pulse 2s ease-in-out infinite; }
          .wr-spin-subtle { animation: wr-pulse 2s ease-in-out infinite; }
        }
      `}</style>

      <Box
        role="status"
        aria-live="polite"
        aria-label={label || "Laden"}
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        {/* Draaiende ring + SVG-windroosbladen */}
        <Box
          className={spinClass}
          sx={{
            width: diameter,
            height: diameter,
            borderRadius: "50%",
            border: `${strokeWidth}px solid`,
            borderColor: subtle ? "rgba(239,125,0,0.25)" : "rgba(239,125,0,0.18)",
            borderTopColor: COLORS.orange,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {!subtle && (
            /* SVG: 4 windroosbladen + "W" in het midden */
            <svg
              width={diameter * 0.58}
              height={diameter * 0.58}
              viewBox="0 0 40 40"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              {/* Bovenste blad — oranje */}
              <path
                d="M20 20 C20 20 14 14 14 8 C14 4 17 2 20 2 C23 2 26 4 26 8 C26 14 20 20 20 20Z"
                fill={COLORS.orange}
                opacity="0.95"
              />
              {/* Rechter blad — rood */}
              <path
                d="M20 20 C20 20 26 14 32 14 C36 14 38 17 38 20 C38 23 36 26 32 26 C26 26 20 20 20 20Z"
                fill={COLORS.red}
                opacity="0.85"
              />
              {/* Onderste blad — oranje */}
              <path
                d="M20 20 C20 20 26 26 26 32 C26 36 23 38 20 38 C17 38 14 36 14 32 C14 26 20 20 20 20Z"
                fill={COLORS.orange}
                opacity="0.7"
              />
              {/* Linker blad — rood */}
              <path
                d="M20 20 C20 20 14 26 8 26 C4 26 2 23 2 20 C2 17 4 14 8 14 C14 14 20 20 20 20Z"
                fill={COLORS.red}
                opacity="0.6"
              />
              {/* "W" in het midden */}
              <text
                x="20"
                y="24"
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                fontFamily="windroos, Georgia, serif"
                fill="white"
              >
                W
              </text>
            </svg>
          )}
        </Box>

        {/* Zichtbaar label (of visueel verborgen als null) */}
        {label !== null && label !== undefined ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "windroos, Georgia, serif" }}
          >
            {label}
          </Typography>
        ) : (
          /* Visueel verborgen fallback tekst voor screenreaders */
          <Box
            component="span"
            sx={{
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0,0,0,0)",
              whiteSpace: "nowrap",
            }}
          >
            Laden
          </Box>
        )}
      </Box>
    </>
  );
}
